// 오프라인 지출 장부. 보상(v1)은 읽기만 하고 거래 집합과 장착 레지스터만 v2에 쓴다.
import { Rng } from '../core/rng';
import { DEFAULT_WEAPON, GOLD_MAX, STARTER_GRANT, itemById, reforgedBases, upgradeOf } from '../shared/rpg';
import type { GameCommand, ProfileMode, ProfileView, TxCode, TxKind } from '../shared/types';
import { browserCommerceLock, type CommerceLockProvider, type CommerceLockState } from './commerceLock';
import { SAVE_KEY, SaveStore, type SaveStorage } from './save';
import { storageEventMatches } from '../characters/keys';

export const PROFILE_KEY = 'dungeonhero.profile.v2';
export const PROFILE_BACKUP_KEY = PROFILE_KEY + '.bak';
export const PROFILE_LATEST_BACKUP_KEY = PROFILE_BACKUP_KEY + '.latest';
export type ProfileCommand = Extract<GameCommand, { type: 'buy' | 'equip' | 'setSkillFocus' | 'recoverProfile' }>;
interface Purchase { id: string; itemId: string; price: number; at: number }
interface Equipment { weapon: string; armor: string | null; focus: number; rev: number; by: string }
interface ProfileData { version: 2; starter: { granted: true; amount: number } | null; txs: Purchase[]; equip: Equipment }
export interface ProfileOptions {
  clock?: () => number; nonce?: string; locks?: CommerceLockProvider | null;
  /** 장비 경제 순지출 골드(dungeonhero.gear.v1 강화·주문서 − 판매, D2b). 잔액 = 획득 + 준비금 − 상점 구매 − 이것. 없으면 0 */
  gearNet?: () => number;
}
const record = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v);
const integer = (v: unknown, max = Number.MAX_SAFE_INTEGER): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= max;
const compare = (a: string, b: string): number => a < b ? -1 : a > b ? 1 : 0;
let nonceSerial = 0;
function tabNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const now = Date.now(), serial = ++nonceSerial, rng = new Rng(now ^ serial);
  return `${now.toString(36)}-${serial}-${rng.next().toString(36).slice(2)}`;
}
function empty(grant: boolean): ProfileData {
  return { version: 2, starter: grant ? { granted: true, amount: STARTER_GRANT } : null, txs: [],
    equip: { weapon: DEFAULT_WEAPON, armor: null, focus: 0, rev: 0, by: '' } };
}
function valid(data: unknown): data is ProfileData {
  if (!record(data) || data.version !== 2 || !Array.isArray(data.txs) || !record(data.equip)) return false;
  const s = data.starter, e = data.equip;
  if (s !== null && (!record(s) || s.granted !== true || !integer(s.amount, GOLD_MAX))) return false;
  if (typeof e.weapon !== 'string' || (itemById(e.weapon) && itemById(e.weapon)?.slot !== 'weapon') ||
    (e.armor !== null && (typeof e.armor !== 'string' || (itemById(e.armor) && itemById(e.armor)?.slot !== 'armor'))) ||
    !integer(e.focus, 3) || !integer(e.rev) || typeof e.by !== 'string') return false;
  const ids = new Set<string>();
  for (const tx of data.txs) {
    if (!record(tx) || typeof tx.id !== 'string' || tx.id.length === 0 || ids.has(tx.id) ||
      typeof tx.itemId !== 'string' || !integer(tx.price, GOLD_MAX) || !integer(tx.at)) return false;
    ids.add(tx.id);
  }
  return true;
}
/** profile.v2 선택 필드 gearUnified(전환한 옛 장비 id, ECONOMY 14절). 문자열이 아닌 값은 버린다 */
function unifiedOf(d: ProfileData): string[] {
  const v = (d as unknown as Record<string, unknown>).gearUnified;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0 && x.length < 64).slice(0, 32) : [];
}
function merge(a: ProfileData, b: ProfileData): ProfileData {
  const txs = new Map(a.txs.map(tx => [tx.id, tx]));
  for (const tx of b.txs) {
    const old = txs.get(tx.id);
    if (old && (old.itemId !== tx.itemId || old.price !== tx.price || old.at !== tx.at)) throw new Error('거래 ID 충돌');
    txs.set(tx.id, tx);
  }
  const ae = a.equip, be = b.equip;
  const aWins = ae.rev > be.rev || (ae.rev === be.rev && compare(ae.by, be.by) >= 0);
  // 장비 통합(ECONOMY 14절) 전환 기록은 합집합(탭 두 개가 각자 전환해도 한쪽 기록이 사라지지 않게). 없으면 필드를 만들지 않는다
  const unified = unifiedOf(a), unifiedB = unifiedOf(b);
  const gu = unified.length || unifiedB.length ? { gearUnified: [...new Set([...unified, ...unifiedB])].sort() } : {};
  return { ...a, ...b, ...gu, version: 2, starter: a.starter === null || b.starter === null ? null : a.starter,
    txs: [...txs.values()].sort((x, y) => x.at - y.at || compare(x.id, y.id)),
    equip: aWins ? { ...be, ...ae } : { ...ae, ...be } };
}

export class ProfileStore implements ProfileView {
  mode: ProfileMode = 'ok';
  revision = 0;
  gold = 0;
  pendingGold = 0;
  owned: readonly string[] = [DEFAULT_WEAPON];
  reforged: readonly string[] = [];
  weapon: string = DEFAULT_WEAPON;
  armor: string | null = null;
  skillFocus = 0;
  starterGranted = false;
  /** 장비 통합으로 gear.v1에 옮긴 옛 장비 id(profile.v2 gearUnified, ECONOMY 14절) */
  gearUnified: readonly string[] = [];
  lastSaveOk = true;
  readonly last = { seq: 0, kind: 'none' as TxKind, code: 'ok' as TxCode, itemId: '', requestId: '' };
  private data: ProfileData | null = null;
  private lastRaw = '';
  private loaded = false;
  // v1 손상 등으로 지출이 획득을 넘더라도 성공한 구매의 소유는 유지한다.
  private balanceDeficit = false;
  private readonly seen = new Set<string>();
  private readonly clock: () => number;
  private readonly gearNet: () => number;
  private readonly nonce: string;
  private readonly storage: SaveStorage;
  private earned = 0;
  private storageMode: ProfileMode = 'ok';
  private commerceState: CommerceLockState = 'waiting';
  private pendingCommerceState: CommerceLockState = 'waiting';
  private readonly releaseLock: () => void;
  private dirty = true;
  private inCommand = false;
  private ioFault = false;
  private readonly onStorage = (event: StorageEvent): void => {
    // 캐릭터 저장소면 실제 영역·캐릭터 키로 비교한다(src/characters/keys.ts)
    if (storageEventMatches(this.storage, event, PROFILE_KEY, SAVE_KEY)) this.dirty = true;
  };

  constructor(private readonly save: SaveStore, storage?: SaveStorage, options: ProfileOptions = {}) {
    this.storage = storage ?? save.backingStorage;
    this.clock = options.clock ?? Date.now;
    this.gearNet = options.gearNet ?? (() => 0);
    this.nonce = options.nonce ?? tabNonce();
    // SaveStore의 메모리 대체와 별개로, 브라우저 저장 정책 차단은 경제 잠금이다.
    if (!storage) {
      try { if (typeof window !== 'undefined') void window.localStorage; }
      catch { this.lock('unavailable'); }
    }
    const locks = options.locks === undefined ? browserCommerceLock() : options.locks;
    if (locks) this.releaseLock = locks.acquire(state => { this.pendingCommerceState = state; });
    else { this.pendingCommerceState = 'unavailable'; this.releaseLock = () => {}; }
    this.tick();
    if (typeof window !== 'undefined') window.addEventListener('storage', this.onStorage);
  }

  dispose(): void {
    this.releaseLock();
    this.pendingCommerceState = 'unavailable';
    if (typeof window !== 'undefined') window.removeEventListener('storage', this.onStorage);
  }

  /** GameSim의 고정 틱 시작에서만 비동기 신호를 적용한다. 평상시에는 비교만 한다. */
  tick(): void {
    if (this.commerceState !== this.pendingCommerceState) {
      this.commerceState = this.pendingCommerceState;
      this.updateMode(); this.dirty = true;
    }
    this.updateMode();
    if (this.dirty) { this.dirty = false; this.sync(); }
  }

  private updateMode(): void {
    const mode: ProfileMode = this.storageMode === 'unavailable' || this.commerceState === 'unavailable'
      ? 'unavailable' : this.commerceState === 'waiting' ? 'other_tab' : this.storageMode;
    if (this.mode !== mode) { this.mode = mode; this.revision++; }
  }

  private ioFailure(): void {
    this.ioFault = true; this.lastSaveOk = false; this.revision++;
    if (!this.loaded && !this.inCommand) this.lock('unavailable');
    else this.dirty = true;
  }

  private lock(mode: ProfileMode): void {
    this.storageMode = mode; this.updateMode(); this.lastSaveOk = false; this.revision++;
    if (mode === 'corrupt' || mode === 'future') {
      this.weapon = DEFAULT_WEAPON; this.armor = null; this.skillFocus = 0;
      this.owned = [DEFAULT_WEAPON]; this.reforged = []; this.gold = 0;
    }
  }

  private corrupt(raw: string): null {
    this.lock('corrupt');
    try {
      if (this.commerceState === 'held') {
        const first = this.storage.getItem(PROFILE_BACKUP_KEY);
        if (first === null) this.storage.setItem(PROFILE_BACKUP_KEY, raw);
        else if (first !== raw && this.storage.getItem(PROFILE_LATEST_BACKUP_KEY) !== raw)
          this.storage.setItem(PROFILE_LATEST_BACKUP_KEY, raw);
      }
    } catch { this.ioFailure(); }
    return null;
  }

  private read(): ProfileData | null {
    let raw: string | null;
    try { raw = this.storage.getItem(PROFILE_KEY); }
    catch { this.ioFailure(); return null; }
    this.lastRaw = raw ?? '';
    if (raw === null) return null;
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { return this.corrupt(raw); }
    if (record(parsed) && typeof parsed.version === 'number' && parsed.version > 2) { this.lock('future'); return null; }
    if (!valid(parsed)) return this.corrupt(raw);
    return parsed;
  }

  private readGold(): boolean {
    try {
      const raw = this.storage.getItem(SAVE_KEY);
      let parsed: unknown = null;
      try { if (raw !== null) parsed = JSON.parse(raw); } catch { /* v1 손상 처리 의미는 SaveStore에 맡긴다. */ }
      this.earned = record(parsed) && integer(parsed.gold) ? parsed.gold : this.save.confirmedGold;
      return true;
    } catch { this.ioFailure(); return false; }
  }

  /** 명령·맵 이동·storage 이벤트에서만 호출한다. 매 틱 복사나 저장은 하지 않는다. */
  sync(): boolean {
    const pending = this.save.data.gold - this.save.confirmedGold;
    if (this.pendingGold !== pending) { this.pendingGold = pending; this.revision++; }
    if (this.storageMode === 'unavailable' || this.commerceState === 'unavailable') return false;
    this.ioFault = false;
    const remote = this.read();
    if (this.ioFault) return false;
    if (remote && this.storageMode !== 'ok') { this.storageMode = 'ok'; this.updateMode(); }
    if (this.storageMode !== 'ok' || !this.readGold()) return false;
    let next: ProfileData;
    try { next = this.data ? merge(this.data, remote ?? this.data) : remote ? merge(remote, remote) : empty(true); }
    catch { this.corrupt(this.lastRaw); return false; }
    if (!remote || JSON.stringify(merge(remote, remote)) !== JSON.stringify(next)) {
      if (this.commerceState !== 'held') {
        // 다른 탭은 준비금 생성·백업·합집합 복원까지 포함해 v2에 쓰지 않는다.
        if (remote) { this.data = next; this.project(); }
        this.loaded = true;
        return true;
      }
      if (!this.persist(next)) return false;
    } else this.data = next;
    this.project();
    this.loaded = true;
    // 일시적 읽기 실패 뒤 원격 장부와 현재 값이 다시 일치하면 실패 표시도 회복한다.
    if (!this.lastSaveOk) { this.lastSaveOk = true; this.revision++; }
    return true;
  }

  private project(): void {
    const data = this.data;
    if (!data || this.storageMode !== 'ok') return;
    // 장비 장부(강화·보호 주문서 지출 − 판매 수입)는 profile.v2 형식을 바꾸지 않고 따로 뺀다(D2b, 이전 빌드는 이 키를 모른다)
    let gold = this.earned + (data.starter?.amount ?? 0) - this.gearNet();
    const ledger = [DEFAULT_WEAPON] as string[];
    for (const tx of data.txs) {
      if (!itemById(tx.itemId)?.seller || ledger.includes(tx.itemId)) continue;
      gold -= tx.price; ledger.push(tx.itemId);
    }
    // 단조 개선(ECONOMY 11절): 개선 장비를 가지면 그 원본은 개선 장비가 되었으므로 소유에서 뺀다. 저장값이 아니라 계산이다.
    const reforged = reforgedBases(ledger);
    const owned = ledger.filter((id) => !reforged.includes(id));
    this.balanceDeficit = gold < 0;
    gold = Math.max(0, gold);
    // 개선 전 원본을 장착한 기록(다른 탭·호환 빌드가 쓴 장착)은 그 원본이 된 개선 장비로 읽는다.
    const upgraded = reforged.includes(data.equip.weapon) ? upgradeOf(data.equip.weapon)?.id : undefined;
    const wanted = upgraded ?? data.equip.weapon;
    const weapon = owned.includes(wanted) ? wanted : DEFAULT_WEAPON;
    const armor = data.equip.armor !== null && owned.includes(data.equip.armor) ? data.equip.armor : null;
    const pending = this.save.data.gold - this.save.confirmedGold;
    const changed = this.gold !== gold || this.pendingGold !== pending || this.weapon !== weapon || this.armor !== armor ||
      this.skillFocus !== data.equip.focus || !this.starterGranted || this.owned.join(',') !== owned.join(',') ||
      this.reforged.join(',') !== reforged.join(',');
    this.gold = gold; this.pendingGold = pending; this.weapon = weapon; this.armor = armor;
    this.skillFocus = data.equip.focus; this.starterGranted = true;
    const unified = unifiedOf(data);
    if (unified.join(',') !== this.gearUnified.join(',')) { this.gearUnified = unified; this.revision++; }
    if (this.owned.join(',') !== owned.join(',')) this.owned = owned;
    if (this.reforged.join(',') !== reforged.join(',')) this.reforged = reforged;
    if (changed) this.revision++;
  }

  /** 이 탭이 거래 권한을 갖고 장부를 읽은 상태(장비 통합 전환을 쓸 수 있다) */
  get writable(): boolean { return this.loaded && this.mode === 'ok' && this.commerceState === 'held' && this.data !== null; }

  /**
   * 장비 통합 전환 기록을 profile.v2에 남긴다(ECONOMY 14절). 기존 필드는 그대로 두고 gearUnified만 합집합으로 더한다.
   * 거래 권한이 없거나 쓰기에 실패하면 false(다음 틱에 다시 시도한다)
   */
  markGearUnified(ids: readonly string[]): boolean {
    if (!this.writable || !this.sync() || !this.data) return false;
    const have = unifiedOf(this.data);
    const all = [...new Set([...have, ...ids])].sort();
    if (all.join(',') === have.join(',')) return true;
    const next = { ...this.data, gearUnified: all } as ProfileData;
    if (!this.persist(next)) return false;
    this.project();
    return true;
  }

  /** 저장소를 읽지 않고 잔액·장비만 다시 계산한다(장비 장부가 바뀐 뒤 GameSim이 부른다) */
  recompute(): void { this.project(); }

  private persist(next: ProfileData): boolean {
    if (this.commerceState !== 'held') return false;
    try { this.storage.setItem(PROFILE_KEY, JSON.stringify(next)); }
    catch { this.ioFailure(); return false; }
    // setItem 성공은 확정이다. 이후 읽기 실패를 실패 거래로 보고했다가 조용히 확정하지 않는다.
    this.data = next;
    const verified = this.read();
    if (verified) {
      try { this.data = merge(next, verified); }
      catch { this.corrupt(this.lastRaw); return true; }
    }
    if (!verified || JSON.stringify(merge(verified, verified)) !== JSON.stringify(this.data)) {
      // 정상 writer 락 밖의 옛 클라이언트·수동 편집에 대한 방어. 다음 틱에도 재확인한다.
      this.dirty = true;
      if (this.storageMode === 'ok' && verified) {
        try { this.storage.setItem(PROFILE_KEY, JSON.stringify(this.data)); }
        catch { /* 이미 성공한 거래는 메모리에 유지하고 다음 동기화에서 복원한다. */ }
      }
    }
    this.lastSaveOk = true;
    return true;
  }

  /** 문맥 검증은 GameSim이 결정한다. 실패도 seq는 증가하지만 장비·잔액은 바꾸지 않는다. */
  command(cmd: ProfileCommand, denied?: TxCode): TxCode {
    const kind: TxKind = cmd.type === 'setSkillFocus' ? 'focus' : cmd.type === 'recoverProfile' ? 'recover' : cmd.type;
    let code: TxCode;
    if (this.seen.has(cmd.requestId)) code = 'duplicate';
    else {
      this.seen.add(cmd.requestId);
      this.inCommand = true; this.ioFault = false;
      try {
        if (denied) code = denied;
        else if (this.commerceState !== 'held') code = 'readonly';
        else if (cmd.type === 'recoverProfile') code = this.recover();
        else if (this.storageMode !== 'ok') code = 'readonly';
        else if (!this.sync()) code = this.storageMode === 'ok' ? 'save_failed' : 'readonly';
        else code = this.change(cmd);
      } finally { this.inCommand = false; }
    }
    this.last.seq++; this.last.kind = kind; this.last.code = code;
    this.last.itemId = 'itemId' in cmd ? cmd.itemId ?? '' : '';
    this.last.requestId = cmd.requestId; this.revision++;
    return code;
  }

  private change(cmd: Exclude<ProfileCommand, { type: 'recoverProfile' }>): TxCode {
    const data = this.data!;
    let next: ProfileData;
    if (cmd.type === 'buy') {
      const item = itemById(cmd.itemId);
      if (!item) return 'unknown_item';
      if (this.owned.includes(item.id) || this.reforged.includes(item.id)) return 'owned';
      if (!item.seller) return 'unknown_item';
      if (item.upgradeFrom && !this.owned.includes(item.upgradeFrom)) return 'needs_base';
      if (this.balanceDeficit || item.price > this.gold) return 'insufficient';
      const at = this.clock();
      if (!integer(at)) return 'save_failed';
      const tx: Purchase = { id: JSON.stringify([cmd.requestId, this.nonce]), itemId: item.id, price: item.price, at };
      next = merge(data, { ...data, txs: [tx] });
      // 장착 중인 원본을 개선하면 같은 쓰기에서 개선 장비를 장착한다(원본이 그 장비가 되었다).
      if (item.upgradeFrom && data.equip.weapon === item.upgradeFrom) {
        const rev = data.equip.rev + 1;
        if (!integer(rev)) return 'readonly';
        next = { ...next, equip: { ...next.equip, weapon: item.id, rev, by: this.nonce } };
      }
    } else {
      const equip = { ...data.equip, rev: data.equip.rev + 1, by: this.nonce };
      if (!integer(equip.rev)) return 'readonly';
      if (cmd.type === 'setSkillFocus') {
        if (!integer(cmd.skillId, 3)) return 'unknown_item';
        equip.focus = cmd.skillId;
      } else {
        if (cmd.slot !== 'weapon' && cmd.slot !== 'armor') return 'slot_mismatch';
        if (cmd.itemId === null) {
          if (cmd.slot !== 'armor') return 'slot_mismatch';
          equip.armor = null;
        } else {
          const item = itemById(cmd.itemId);
          if (!item) return 'unknown_item';
          if (item.slot !== cmd.slot) return 'slot_mismatch';
          if (!this.owned.includes(item.id)) return 'not_owned';
          equip[cmd.slot] = item.id;
        }
      }
      next = { ...data, equip };
    }
    if (!this.persist(next)) return 'save_failed';
    this.project();
    // 저장된 거래를 실패라 알린 뒤 나중에 조용히 확정하지 않는다. 소유는 결정적 장부 계산을 따른다.
    return 'ok';
  }

  private recover(): TxCode {
    if (this.mode !== 'corrupt') return 'readonly';
    // 복구 직전 다른 탭이 고쳤거나 미래 버전을 썼으면 덮어쓰지 않는다.
    const remote = this.read();
    if (this.ioFault) return 'save_failed';
    if (this.mode !== 'corrupt') return 'readonly';
    if (remote) {
      // 형식은 정상이지만 같은 ID의 내용이 충돌한 원문도 복구 전에 보존한다.
      try { if (this.data) merge(this.data, remote); return 'readonly'; }
      catch { this.corrupt(this.lastRaw); }
      if (this.ioFault) return 'save_failed';
    }
    this.storageMode = 'ok'; this.updateMode();
    if (!this.readGold() || !this.persist(this.data ?? empty(false))) { this.storageMode = 'corrupt'; this.updateMode(); return 'save_failed'; }
    this.project();
    this.loaded = true;
    return 'ok';
  }
}
